import { useQuery } from '@tanstack/react-query'
import { apiGet } from './api.ts'

/**
 * A campaign's headline numbers, for the My campaigns cards and the campaign
 * Overview. The same aggregate reports the Progress page reads (§6d: aggregate,
 * so they stay available through the analysis window), not a second source.
 */

export interface Totals {
  people: string
  reached: string
  supporters: string
  opposed: string
  events: string
  canvassers: string
  first_contact: string | null
  last_contact: string | null
}

export interface MethodRow {
  method: string
  events: string
  people: string
  reached: string
}

export function useCampaignTotals(campaignId: string | undefined, enabled = true) {
  const totals = useQuery({
    queryKey: ['contact-totals', campaignId],
    queryFn: () => apiGet<Totals>(`/contacts/totals?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId) && enabled,
  })
  const methods = useQuery({
    queryKey: ['contact-by-method', campaignId],
    queryFn: () => apiGet<{ methods: MethodRow[] }>(`/contacts/by-method?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId) && enabled,
  })

  const byMethod = (m: string) => {
    const row = methods.data?.methods.find((r) => r.method === m)
    return row ? Number(row.events) : 0
  }

  return {
    isPending: totals.isPending,
    error: totals.error ?? methods.error,
    supporters: totals.data ? Number(totals.data.supporters) : null,
    reached: totals.data ? Number(totals.data.reached) : null,
    lastContact: totals.data?.last_contact ?? null,
    // Attempts, not people: a door knocked twice is two knocks. Progress keeps
    // the STANDING vs ACTIVITY distinction (decisions 2026-09-2x); these are
    // activity, and labelled so.
    doors: methods.data ? byMethod('walk') : null,
    calls: methods.data ? byMethod('phone') : null,
  }
}
